"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { useConfiguratorPrice } from "@/hooks/useConfiguratorPrice";

interface Props {
  onNext: () => void;
  onBack?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Shown when the Continue button is disabled, so the blocker is visible. */
  nextHint?: string;
}

/**
 * Shared sticky action bar for configurator steps: Back, live per-set price,
 * Continue. The price appears once a line is chosen and updates as choices
 * change, so cost is never a step-6 surprise.
 */
export function ConfiguratorFooter({ onNext, onBack, nextLabel = "Continue", nextDisabled, nextHint }: Props) {
  const { quote, status, message } = useConfiguratorPrice();

  return (
    <div
      // The full-bleed negative margin must match .container-main's padding at
      // every breakpoint (1rem below 380px, 1.5rem to 768px, 2rem above). It
      // was pinned at 1.5rem, so a 320px viewport scrolled sideways by 8px.
      className="sticky bottom-0 z-20 border-t mt-8 -mx-4 px-4 min-[380px]:-mx-6 min-[380px]:px-6 md:-mx-8 md:px-8"
      style={{
        backgroundColor: "#0A0A0AF2",
        borderColor: "#2A2A2A",
        backdropFilter: "blur(8px)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {nextDisabled && nextHint ? (
        <p className="pt-3 text-xs text-center" role="status" style={{ color: "#9A9A9A" }}>
          {nextHint}
        </p>
      ) : null}
      {/* The bar used to show nothing at all when pricing was refused, so a
          customer who picked an unapproved gauge saw the price silently
          disappear. Say why, here, at the point of the decision. */}
      {status === "error" && message ? (
        <p className="pt-3 text-xs text-center" role="status" style={{ color: "#E8A0A0" }}>
          {message}
        </p>
      ) : null}
      <div className="flex items-center gap-3 sm:gap-4 py-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 text-sm font-bold border rounded-sm transition-colors hover:border-[#E8C547] hover:text-[#E8C547] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
            style={{ borderColor: "#3A3A3A", color: "#C0C0C0" }}
          >
            <ArrowLeft size={15} aria-hidden />
            Back
          </button>
        ) : null}

        {status === "ready" && quote ? (
          <p className="text-sm whitespace-nowrap px-1" aria-live="polite">
            <span className="font-bold" style={{ color: "#E8C547" }}>
              {formatPrice(quote.unitPrice)}
            </span>
            <span className="ml-1" style={{ color: "#8A8A8A" }}>/ set</span>
          </p>
        ) : status === "loading" ? (
          <p className="text-sm px-1" style={{ color: "#8A8A8A" }} aria-hidden>
            …
          </p>
        ) : null}

        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-sm transition-all hover:brightness-110 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
          style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
        >
          {nextLabel}
          <ArrowRight size={15} aria-hidden />
        </button>
      </div>
    </div>
  );
}
